import { useSession } from '@/hooks/useSession';
import { useCallback } from 'react';
import { requestDemoMode, disableDemoMode } from '@/lib/demo-mode';

/**
 * Hook for managing demo mode state and operations
 * 
 * @returns {Object} Demo mode state and methods
 * @returns {boolean} returns.enabled - Whether demo mode is enabled for current user
 * @returns {boolean} returns.loading - Whether demo mode status is loading
 * @returns {() => Promise<void>} returns.enable - Enable demo mode for current user
 * @returns {() => Promise<void>} returns.disable - Disable demo mode for current user
 * 
 * @example
 * ```tsx
 * const { enabled, enable, disable } = useDemoMode();
 * 
 * if (enabled) {
 *   return <DemoContent />;
 * }
 * 
 * return <Button onClick={enable}>Enable Demo Mode</Button>;
 * ```
 */
export function useDemoMode() {
  const { demoModeEnabled: enabled, demoModeLoading: loading, user } = useSession();

  const enable = useCallback(async () => {
    if (!user) {
      throw new Error('User must be authenticated to enable demo mode');
    }
    return requestDemoMode('User requested demo mode');
  }, [user]);

  const disable = useCallback(async () => {
    if (!user) {
      throw new Error('User must be authenticated to disable demo mode');
    }
    return disableDemoMode('User disabled demo mode');
  }, [user]);

  return {
    enabled,
    loading,
    enable,
    disable,
  };
}
